import { nextTick, ref, type Ref } from 'vue';
import type { QBtn } from 'quasar';

/**
 * Keyboard and focus behaviour of a `q-menu` opened from a `q-btn`, following the WAI-ARIA menu button pattern:
 * opening focuses the first item, ArrowDown and ArrowUp move between the items and wrap, Home and End jump to the
 * ends, and Escape closes. A dismissed menu gives focus back to the trigger; choosing an item leaves it to whatever the
 * item opens, a dialog or a page.
 *
 * Bind `triggerRef` to the `q-btn`, `isMenuOpen` to the menu's `v-model`, `onMenuShow` and `onMenuHide` to its `@show`
 * and `@hide`, and `onMenuKeydown` to the `role="menu"` element. Each item closes the menu with `closeOnChoice`.
 * `itemRefs` are the menu items, in order.
 *
 * The `q-menu` needs `no-refocus`. Otherwise Quasar puts focus back on the trigger the moment an item closes the menu,
 * while the Enter that chose it is still down, and the key's release on the trigger opens the menu again.
 */
export function useMenuKeyboardNavigation(itemRefs: Ref<HTMLElement | null>[]) {
  const isMenuOpen = ref(false);
  const triggerRef = ref<InstanceType<typeof QBtn> | null>(null);
  let itemChosen = false;

  const getMenuItems = () => itemRefs.map((itemRef) => itemRef.value).filter(Boolean) as HTMLElement[];

  function focusTrigger() {
    const triggerEl = triggerRef.value?.$el as HTMLElement | undefined;
    const focusTarget = triggerEl?.matches('button')
      ? triggerEl
      : triggerEl?.querySelector<HTMLElement>('button, [tabindex], [role="button"]');

    focusTarget?.focus();
  }

  function closeOnChoice() {
    itemChosen = true;
    isMenuOpen.value = false;
  }

  function onMenuHide() {
    if (!itemChosen) focusTrigger();
    itemChosen = false;
  }

  async function onMenuShow() {
    itemChosen = false;
    await nextTick();
    requestAnimationFrame(() => {
      getMenuItems()[0]?.focus();
    });
  }

  function onMenuKeydown(event: KeyboardEvent) {
    const items = getMenuItems();
    if (!items.length) return;
    const idx = items.indexOf(document.activeElement as HTMLElement);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      items[(idx + 1) % items.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      items[(idx - 1 + items.length) % items.length]?.focus();
    } else if (event.key === 'Home') {
      event.preventDefault();
      items[0]?.focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      items[items.length - 1]?.focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      isMenuOpen.value = false;
    }
  }

  return { isMenuOpen, triggerRef, closeOnChoice, onMenuShow, onMenuHide, onMenuKeydown };
}
